import React, { useState } from "react";
import { Create } from "@refinedev/antd";
import { Form, Input, Select, Upload, Button, message, Space, Card } from "antd";
import { UploadOutlined, FileTextOutlined } from "@ant-design/icons";
import { useCreate } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import { CREATE_DOCUMENT_TEMPLATE_MUTATION } from "./queries";

const deriveFileApiBase = () => {
  return import.meta.env.VITE_API_URL
};

const fileUploadUrl = `${deriveFileApiBase()}/file-storage/upload`;

export const DocumentTemplateCreate: React.FC = () => {
  const [form] = Form.useForm();
  const { mutate: createDocumentTemplate } = useCreate();
  const navigate = useNavigate();
  const [uploadedFile, setUploadedFile] = useState<any>(null);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!uploadedFile) {
        message.error("Please upload a template file first");
        return;
      }

      await createDocumentTemplate({
        resource: "documentTemplates",
        values: {
          ...values,
          fileId: uploadedFile.fileId,
          url: uploadedFile.url,
        },
        meta: {
          gqlMutation: CREATE_DOCUMENT_TEMPLATE_MUTATION,
        },
      });
      message.success("Document template created successfully");
      navigate("/document-templates");
    } catch (error: any) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      message.error("Failed to create document template");
    }
  };

  const uploadProps = {
    name: "file",
    customRequest: async (options: any) => {
      const { file, onSuccess, onError } = options;
      try {
        const fd = new FormData();
        fd.append("file", file as Blob);
        const resp = await fetch(fileUploadUrl, {
          method: "POST",
          body: fd,
          credentials: "include",
        });
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(txt || `Upload failed: ${resp.status}`);
        }
        const json = await resp.json();
        const fileData = json?.data;
        if (!fileData || !fileData.fileId) {
          throw new Error("Upload response missing file data or fileId");
        }
        setUploadedFile(fileData);
        onSuccess?.(json, file);
        message.success("File uploaded successfully");
      } catch (e: any) {
        console.error(e);
        message.error(e?.message || "Upload failed");
        onError?.(e);
      }
    },
    showUploadList: false,
    accept: ".docx,.pdf,.odt,.ods,.xlsx,.xml,.md,.txt,.doc",
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    message.success("File removed");
  };

  return (
    <Create
      title="Create Document Template"
      footerButtons={() => (
        <Button type="primary" onClick={handleSave}>
          Save
        </Button>
      )}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="documentName"
          label="Document Name"
          rules={[{ required: true, message: "Please enter document name" }]}
        >
          <Input placeholder="Enter document name" />
        </Form.Item>

        <Form.Item
          name="category"
          label="Category"
          rules={[{ required: true, message: "Please select category" }]}
        >
          <Select placeholder="Select category">
            <Select.Option value="CONTRACT">Contract</Select.Option>
            <Select.Option value="AGREEMENT">Agreement</Select.Option>
              <Select.Option value="MINUTE">Minute</Select.Option>
            <Select.Option value="POLICY">Policy</Select.Option>
            <Select.Option value="FORM">Form</Select.Option>
            <Select.Option value="REPORT">Report</Select.Option>
            <Select.Option value="INVOICE">Invoice</Select.Option>
            <Select.Option value="OTHER">Other</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="tag"
          label="Tag"
          rules={[{ required: true, message: "Please select tag" }]}
        >
          <Select placeholder="Select tag">
            <Select.Option value="LEGAL">Legal</Select.Option>
            <Select.Option value="FINANCE">Finance</Select.Option>
            <Select.Option value="HR">HR</Select.Option>
            <Select.Option value="PROCUREMENT">Procurement</Select.Option>
            <Select.Option value="ADMIN">Admin</Select.Option>
            <Select.Option value="GENERAL">General</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item name="fileId" style={{ display: 'none' }}>
          <Input />
        </Form.Item>

        <Form.Item name="url" style={{ display: 'none' }}>
          <Input />
        </Form.Item>

        <Card
          title="Template File"
          style={{ marginTop: 16 }}
          extra={
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />}>
                Upload Template File
              </Button>
            </Upload>
          }
        >
          {uploadedFile ? (
            <div style={{ padding: 16 }}>
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <Space>
                  <FileTextOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                  <a href={uploadedFile.url} target="_blank" rel="noreferrer" style={{ fontWeight: 500 }}>
                    {uploadedFile.originalName}
                  </a>
                </Space>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  File ID: {uploadedFile.fileId}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Size: {((uploadedFile.size || 0) / 1024).toFixed(2)} KB
                </div>
                <Button
                  size="small"
                  danger
                  onClick={handleRemoveFile}
                  style={{ marginTop: 8 }}
                >
                  Remove File
                </Button>
              </Space>
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
              <FileTextOutlined style={{ fontSize: '24px', marginBottom: 8 }} />
              <div>No template file uploaded yet</div>
              <div style={{ fontSize: '12px' }}>Click "Upload Template File" to select a document template</div>
              <div style={{ fontSize: '12px', marginTop: 4 }}>
                Supported formats: .docx, .odt, .ods, .xlsx, .xml, .md, .pdf, .txt, .doc
              </div>
            </div>
          )}
        </Card>
      </Form>
    </Create>
  );
};